import { supabase } from '../lib/supabaseClient';

export interface BlueprintExecutionMetrics {
  blueprintId: string;
  organizationId: string;
  blueprintName: string | null;
  blueprintVersion: number | null;
  blueprintCategory?: string | null;
  blueprintStatus?: string | null;
  generatedTemplateId?: string | null;
  totalExecutions: number;
  totalReprocessed: number;
  reprocessRate: number;
  avgExecutionTime: number | null;
  slaBreachRate: number | null;
  lastExecutionAt: string | null;
}

export interface BlueprintTrendPoint {
  date: string;
  totalExecutions: number;
  totalReprocessed: number;
  reprocessRate: number;
}

export interface BlueprintVersionStagePerformance {
  stage_id: string;
  stage_name: string;
  stage_order: number | null;
  tp_medio: number | null;
  tra_medio: number | null;
  total_tasks: number;
  total_reprocessos: number;
}

export interface BlueprintVersionPerformance {
  blueprint_id: string;
  blueprint_version_id: string;
  version_semver: string;
  total_execucoes: number;
  lead_time_medio: number | null;
  taxa_reprocesso: number;
  sla_percentual: number | null;
  gargalo_principal: string | null;
  tempo_medio_por_etapa: BlueprintVersionStagePerformance[];
}

export interface BlueprintPerformanceDetails {
  metrics: BlueprintExecutionMetrics | null;
  trend: BlueprintTrendPoint[];
  versions: BlueprintVersionPerformance[];
}

const DEFAULT_REPROCESS_THRESHOLD = Number(import.meta.env.VITE_BLUEPRINT_QUALITY_ALERT_THRESHOLD ?? '15');
const DEFAULT_SLA_THRESHOLD = Number(import.meta.env.VITE_BLUEPRINT_SLA_ALERT_THRESHOLD ?? '85');

export const BLUEPRINT_QUALITY_ALERT_THRESHOLD = Number.isFinite(DEFAULT_REPROCESS_THRESHOLD)
  ? DEFAULT_REPROCESS_THRESHOLD
  : 15;
export const BLUEPRINT_SLA_ALERT_THRESHOLD = Number.isFinite(DEFAULT_SLA_THRESHOLD)
  ? DEFAULT_SLA_THRESHOLD
  : 85;

function mapMetricsRow(row: Record<string, any>): BlueprintExecutionMetrics {
  return {
    blueprintId: row.blueprint_id,
    organizationId: row.organization_id,
    blueprintName: row.blueprint_name ?? null,
    blueprintVersion: typeof row.blueprint_version === 'number' ? row.blueprint_version : null,
    blueprintCategory: row.blueprint_category ?? null,
    blueprintStatus: row.blueprint_status ?? null,
    generatedTemplateId: row.generated_template_id ?? null,
    totalExecutions: row.total_executions ?? 0,
    totalReprocessed: row.total_reprocessed ?? 0,
    reprocessRate: Number(row.reprocess_rate ?? 0),
    avgExecutionTime: typeof row.avg_execution_time === 'number' ? row.avg_execution_time : null,
    slaBreachRate: typeof row.sla_breach_rate === 'number' ? row.sla_breach_rate : null,
    lastExecutionAt: row.last_execution_at ?? null,
  };
}

function diffMinutes(start?: string | null, end?: string | null): number | null {
  if (!start || !end) return null;
  const diff = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(diff) || diff < 0) return null;
  return diff / (1000 * 60);
}

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function roundMetric(value: number | null): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  return Number(value.toFixed(2));
}

function buildInstanceStageEntryLookup(params: {
  instances: Array<Record<string, any>>;
  tasks: Array<Record<string, any>>;
  events: Array<Record<string, any>>;
}): Map<string, Map<string, string>> {
  const { instances, tasks, events } = params;
  const entryLookup = new Map<string, Map<string, string>>();
  const eventEntryLookup = new Map<string, string>();

  events.forEach((event) => {
    if (!event.instance_id || !event.stage_id || !event.created_at) return;
    if (!['stage_advanced', 'task_started'].includes(String(event.event_type))) return;
    const key = `${event.instance_id}:${event.stage_id}`;
    const current = eventEntryLookup.get(key);
    if (!current || new Date(event.created_at).getTime() < new Date(current).getTime()) {
      eventEntryLookup.set(key, event.created_at);
    }
  });

  const tasksByInstance = new Map<string, Array<Record<string, any>>>();
  tasks.forEach((task) => {
    if (!task.instance_id) return;
    const bucket = tasksByInstance.get(task.instance_id) ?? [];
    bucket.push(task);
    tasksByInstance.set(task.instance_id, bucket);
  });

  instances.forEach((instance) => {
    const instanceTasks = [...(tasksByInstance.get(instance.id) ?? [])].sort((left, right) => {
      const leftOrder = left.stage?.stage_order ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = right.stage?.stage_order ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    });

    const perStage = new Map<string, string>();
    let previousCompletedAt = instance.started_at ?? undefined;

    instanceTasks.forEach((task) => {
      const stageId = task.stage_id as string | undefined;
      if (!stageId) return;
      const eventKey = `${instance.id}:${stageId}`;
      const enteredAt =
        eventEntryLookup.get(eventKey) ??
        previousCompletedAt ??
        task.started_at ??
        task.created_at ??
        instance.started_at ??
        undefined;

      if (enteredAt) {
        perStage.set(stageId, enteredAt);
      }
      if (task.completed_at) {
        previousCompletedAt = task.completed_at;
      }
    });

    entryLookup.set(instance.id, perStage);
  });

  return entryLookup;
}

export async function fetchBlueprintExecutionMetrics(params: {
  organizationId: string;
  blueprintIds: string[];
}): Promise<BlueprintExecutionMetrics[]> {
  const { organizationId, blueprintIds } = params;
  if (!organizationId) throw new Error('organizationId e obrigatorio');
  if (!blueprintIds.length) return [];

  try {
    const { data, error } = await supabase
      .from('vw_blueprint_execution_metrics')
      .select('*')
      .eq('organization_id', organizationId)
      .in('blueprint_id', blueprintIds);

    if (error) throw error;
    return (data ?? []).map(mapMetricsRow);
  } catch {
    return [];
  }
}

export async function getBlueprintExecutionMetrics(
  organizationId: string,
  blueprintId: string,
): Promise<BlueprintExecutionMetrics | null> {
  if (!organizationId) throw new Error('organizationId e obrigatorio');
  if (!blueprintId) throw new Error('blueprintId e obrigatorio');

  try {
    const { data, error } = await supabase
      .from('vw_blueprint_execution_metrics')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('blueprint_id', blueprintId)
      .maybeSingle();

    if (error) throw error;
    return data ? mapMetricsRow(data) : null;
  } catch {
    return null;
  }
}

export async function getBlueprintVersionPerformance(
  organizationId: string,
  blueprintId: string,
): Promise<BlueprintVersionPerformance[]> {
  if (!organizationId) throw new Error('organizationId e obrigatorio');
  if (!blueprintId) throw new Error('blueprintId e obrigatorio');

  const { data: instances, error: instancesError } = await supabase
    .from('workflow_instances_v9')
    .select('id, blueprint_version_id, started_at, completed_at, is_overdue, reprocessed_from_id')
    .eq('organization_id', organizationId)
    .eq('blueprint_id', blueprintId)
    .not('blueprint_version_id', 'is', null);

  if (instancesError) throw instancesError;
  if (!instances || instances.length === 0) return [];

  const instanceIds = instances.map((instance) => instance.id);
  const versionIds = [...new Set(instances.map((instance) => instance.blueprint_version_id).filter(Boolean))] as string[];

  const [{ data: versions, error: versionsError }, { data: tasks, error: tasksError }, { data: events, error: eventsError }] =
    await Promise.all([
      supabase
        .from('process_blueprint_versions')
        .select('id, version_semver, created_at')
        .eq('organization_id', organizationId)
        .in('id', versionIds),
      supabase
        .from('workflow_tasks_v9')
        .select(`
          instance_id,
          stage_id,
          stage_name,
          status,
          started_at,
          completed_at,
          created_at,
          stage:workflow_stages_v9(stage_order, name)
        `)
        .eq('organization_id', organizationId)
        .in('instance_id', instanceIds),
      supabase
        .from('workflow_instance_events_v9')
        .select('instance_id, stage_id, event_type, created_at')
        .eq('organization_id', organizationId)
        .in('instance_id', instanceIds)
        .in('event_type', ['stage_advanced', 'task_started', 'task_reprocessed']),
    ]);

  if (versionsError) throw versionsError;
  if (tasksError) throw tasksError;
  if (eventsError) throw eventsError;

  const versionMeta = new Map<string, { versionSemver: string; createdAt: string | null }>();
  (versions ?? []).forEach((version: any) => {
    versionMeta.set(version.id, {
      versionSemver: version.version_semver ?? version.id,
      createdAt: version.created_at ?? null,
    });
  });

  const entryLookup = buildInstanceStageEntryLookup({
    instances: instances as Array<Record<string, any>>,
    tasks: (tasks ?? []) as Array<Record<string, any>>,
    events: (events ?? []) as Array<Record<string, any>>,
  });

  const tasksByInstance = new Map<string, Array<Record<string, any>>>();
  (tasks ?? []).forEach((task: any) => {
    const bucket = tasksByInstance.get(task.instance_id) ?? [];
    bucket.push(task);
    tasksByInstance.set(task.instance_id, bucket);
  });

  const reprocessEventsByStage = new Map<string, number>();
  (events ?? []).forEach((event: any) => {
    if (!event.instance_id || !event.stage_id || event.event_type !== 'task_reprocessed') return;
    const key = `${event.instance_id}:${event.stage_id}`;
    reprocessEventsByStage.set(key, (reprocessEventsByStage.get(key) ?? 0) + 1);
  });

  const buckets = new Map<
    string,
    {
      versionSemver: string;
      createdAt: string | null;
      totalExecutions: number;
      completedLeadTimes: number[];
      totalReprocessed: number;
      slaOnTimeCount: number;
      stages: Map<
        string,
        {
          stageId: string;
          stageName: string;
          stageOrder: number | null;
          leadTimes: number[];
          processingTimes: number[];
          totalTasks: number;
          totalReprocesses: number;
        }
      >;
    }
  >();

  instances.forEach((instance: any) => {
    const versionId = instance.blueprint_version_id as string;
    const meta = versionMeta.get(versionId);
    const bucket =
      buckets.get(versionId) ??
      {
        versionSemver: meta?.versionSemver ?? versionId,
        createdAt: meta?.createdAt ?? null,
        totalExecutions: 0,
        completedLeadTimes: [],
        totalReprocessed: 0,
        slaOnTimeCount: 0,
        stages: new Map(),
      };

    bucket.totalExecutions += 1;
    bucket.totalReprocessed += instance.reprocessed_from_id ? 1 : 0;
    bucket.slaOnTimeCount += instance.is_overdue ? 0 : 1;

    const leadTime = diffMinutes(instance.started_at, instance.completed_at);
    if (leadTime !== null) {
      bucket.completedLeadTimes.push(leadTime);
    }

    const stageEntries = entryLookup.get(instance.id) ?? new Map<string, string>();
    const instanceTasks = [...(tasksByInstance.get(instance.id) ?? [])].sort((left, right) => {
      const leftOrder = left.stage?.stage_order ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = right.stage?.stage_order ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    });

    instanceTasks.forEach((task: any) => {
      const stageId = task.stage_id as string | undefined;
      if (!stageId) return;

      const stageAggregate =
        bucket.stages.get(stageId) ??
        {
          stageId,
          stageName: task.stage?.name ?? task.stage_name ?? stageId,
          stageOrder: task.stage?.stage_order ?? null,
          leadTimes: [],
          processingTimes: [],
          totalTasks: 0,
          totalReprocesses: 0,
        };

      stageAggregate.totalTasks += 1;
      stageAggregate.totalReprocesses += reprocessEventsByStage.get(`${instance.id}:${stageId}`) ?? 0;

      const lead = diffMinutes(stageEntries.get(stageId), task.completed_at);
      if (lead !== null) {
        stageAggregate.leadTimes.push(lead);
      }

      const processing = diffMinutes(task.started_at, task.completed_at);
      if (processing !== null) {
        stageAggregate.processingTimes.push(processing);
      }

      bucket.stages.set(stageId, stageAggregate);
    });

    buckets.set(versionId, bucket);
  });

  return [...buckets.entries()]
    .map(([versionId, bucket]) => {
      const stageRows: BlueprintVersionStagePerformance[] = [...bucket.stages.values()]
        .map((stage) => ({
          stage_id: stage.stageId,
          stage_name: stage.stageName,
          stage_order: stage.stageOrder,
          tp_medio: roundMetric(average(stage.leadTimes)),
          tra_medio: roundMetric(average(stage.processingTimes)),
          total_tasks: stage.totalTasks,
          total_reprocessos: stage.totalReprocesses,
        }))
        .sort((left, right) => (left.stage_order ?? Number.MAX_SAFE_INTEGER) - (right.stage_order ?? Number.MAX_SAFE_INTEGER));

      const bottleneck = [...bucket.stages.values()]
        .map((stage) => ({
          stageName: stage.stageName,
          avgLeadTime: average(stage.leadTimes),
        }))
        .filter((stage) => stage.avgLeadTime !== null)
        .sort((left, right) => (right.avgLeadTime ?? 0) - (left.avgLeadTime ?? 0))[0];

      return {
        blueprint_id: blueprintId,
        blueprint_version_id: versionId,
        version_semver: bucket.versionSemver,
        total_execucoes: bucket.totalExecutions,
        lead_time_medio: roundMetric(average(bucket.completedLeadTimes)),
        taxa_reprocesso: roundMetric((bucket.totalReprocessed / Math.max(bucket.totalExecutions, 1)) * 100) ?? 0,
        sla_percentual: roundMetric((bucket.slaOnTimeCount / Math.max(bucket.totalExecutions, 1)) * 100),
        gargalo_principal: bottleneck?.stageName ?? null,
        tempo_medio_por_etapa: stageRows,
      } satisfies BlueprintVersionPerformance;
    })
    .sort((left, right) => {
      const leftCreatedAt = versionMeta.get(left.blueprint_version_id)?.createdAt;
      const rightCreatedAt = versionMeta.get(right.blueprint_version_id)?.createdAt;
      return new Date(rightCreatedAt ?? 0).getTime() - new Date(leftCreatedAt ?? 0).getTime();
    });
}

export async function getBlueprintPerformanceDetails(params: {
  organizationId: string;
  blueprintId: string;
  templateId?: string | null;
  days?: number;
}): Promise<BlueprintPerformanceDetails> {
  const { organizationId, blueprintId, templateId, days = 30 } = params;
  const [metrics, trend, versions] = await Promise.all([
    getBlueprintExecutionMetrics(organizationId, blueprintId),
    buildTrendSeries({ organizationId, templateId, days }),
    getBlueprintVersionPerformance(organizationId, blueprintId).catch(() => []),
  ]);

  return { metrics, trend, versions };
}

async function buildTrendSeries(params: {
  organizationId: string;
  templateId?: string | null;
  days: number;
}): Promise<BlueprintTrendPoint[]> {
  const { organizationId, templateId, days } = params;
  if (!organizationId || !templateId) return [];

  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  const startDate = since.toISOString();

  const { data, error } = await supabase
    .from('workflow_instances_v9')
    .select('id, started_at')
    .eq('organization_id', organizationId)
    .eq('template_id', templateId)
    .gte('started_at', startDate);

  if (error) throw error;

  const points = new Map<string, { total: number; reprocessed: number }>();
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date(Date.now() - offset * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    points.set(date, { total: 0, reprocessed: 0 });
  }

  (data ?? []).forEach((row) => {
    if (!row.started_at) return;
    const key = String(row.started_at).slice(0, 10);
    if (!points.has(key)) return;
    const entry = points.get(key)!;
    entry.total += 1;
  });

  return Array.from(points.entries()).map(([date, value]) => {
    const { total, reprocessed } = value;
    return {
      date,
      totalExecutions: total,
      totalReprocessed: reprocessed,
      reprocessRate: total > 0 ? Number(((reprocessed / total) * 100).toFixed(2)) : 0,
    };
  });
}

export async function listBlueprintsByReprocessRate(params: {
  organizationId: string;
  limit?: number;
}): Promise<BlueprintExecutionMetrics[]> {
  const { organizationId, limit = 5 } = params;
  if (!organizationId) throw new Error('organizationId e obrigatorio');

  try {
    const { data, error } = await supabase
      .from('vw_blueprint_execution_metrics')
      .select('*')
      .eq('organization_id', organizationId)
      .gt('total_executions', 0)
      .order('reprocess_rate', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return (data ?? []).map(mapMetricsRow);
  } catch {
    return [];
  }
}

export async function ensureQualityAlertsForBlueprints(params: {
  organizationId: string;
  items: BlueprintExecutionMetrics[];
  threshold?: number;
  slaThreshold?: number;
}): Promise<void> {
  const {
    organizationId,
    items,
    threshold = BLUEPRINT_QUALITY_ALERT_THRESHOLD,
    slaThreshold = BLUEPRINT_SLA_ALERT_THRESHOLD,
  } = params;
  if (!organizationId || items.length === 0) return;

  try {
    const flagged = items.filter((item) => {
      const slaCompliance = item.slaBreachRate != null ? 100 - item.slaBreachRate : null;
      return (item.reprocessRate ?? 0) > threshold || (slaCompliance !== null && slaCompliance < slaThreshold);
    });
    if (flagged.length === 0) return;

    const blueprintIds = flagged.map((item) => item.blueprintId);
    const { data: existing, error: existingError } = await supabase
      .from('process_blueprint_logs')
      .select('blueprint_id, created_at')
      .eq('organization_id', organizationId)
      .eq('action', 'BLUEPRINT_QUALITY_ALERT')
      .in('blueprint_id', blueprintIds);

    if (existingError) throw existingError;

    const lastLogMap = new Map<string, string>();
    (existing ?? []).forEach((row) => {
      if (!row.blueprint_id) return;
      const current = lastLogMap.get(row.blueprint_id);
      if (!current || new Date(row.created_at).getTime() > new Date(current).getTime()) {
        lastLogMap.set(row.blueprint_id, row.created_at);
      }
    });

    const versionPerformanceEntries = await Promise.all(
      flagged.map(async (item) => ({
        blueprintId: item.blueprintId,
        versions: await getBlueprintVersionPerformance(organizationId, item.blueprintId).catch(() => []),
      })),
    );
    const versionPerformanceMap = new Map(versionPerformanceEntries.map((entry) => [entry.blueprintId, entry.versions]));

    const payload = flagged
      .filter((item) => {
        if (!item.lastExecutionAt) return false;
        const lastLog = lastLogMap.get(item.blueprintId);
        if (!lastLog) return true;
        return new Date(lastLog).getTime() < new Date(item.lastExecutionAt).getTime();
      })
      .map((item) => {
        const slaCompliance = item.slaBreachRate != null ? 100 - item.slaBreachRate : null;
        const reasons = [
          (item.reprocessRate ?? 0) > threshold ? 'reprocess_rate' : null,
          slaCompliance !== null && slaCompliance < slaThreshold ? 'sla_percentual' : null,
        ].filter((reason): reason is string => reason !== null);
        const versions = versionPerformanceMap.get(item.blueprintId) ?? [];
        const priorityVersion =
          versions.find(
            (version) =>
              version.taxa_reprocesso > threshold ||
              (version.sla_percentual !== null && version.sla_percentual < slaThreshold),
          ) ?? versions[0];

        return {
          blueprint_id: item.blueprintId,
          organization_id: organizationId,
          action: 'BLUEPRINT_QUALITY_ALERT',
          details: {
            reasons,
            reprocess_rate: item.reprocessRate,
            sla_percentual: slaCompliance,
            total_executions: item.totalExecutions,
            total_reprocessed: item.totalReprocessed,
            threshold,
            sla_threshold: slaThreshold,
            blueprint_version_id: priorityVersion?.blueprint_version_id ?? null,
            version_semver: priorityVersion?.version_semver ?? null,
            gargalo_principal: priorityVersion?.gargalo_principal ?? null,
            comparativo_por_versao: versions.slice(0, 5),
          },
        };
      });

    if (!payload.length) return;

    const { error } = await supabase.from('process_blueprint_logs').insert(payload);
    if (error) throw error;
  } catch {
    // process_blueprint_logs table may not exist yet
  }
}
